/**
 * L4 Segmentation page (/l4-segmentation) query string (bookmarkable state).
 *
 * @file Shared parse/build/merge helpers for URLSearchParams on this page.
 */

import L4CategorySortField from 'lib/enums/L4CategorySortField';
import SortDirection from 'lib/enums/SortDirection';

export const L4_SEGMENTATION_TABS = {
  VIEW_RESULTS: 'view_results',
  VIEW_RESEGMENTED: 'view_resegmented',
  SEND_TO_ML: 'send_to_ml',
};

const DEFAULT_PRODUCT_PAGE = 1;
const DEFAULT_PRODUCT_PER_PAGE = 50;
const DEFAULT_ML_PAGE = 1;
const DEFAULT_ML_PER_PAGE = 20;

function parsePositiveInt(raw, fallback) {
  if (raw == null || raw === '') return fallback;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Parse the current location search string into structured L4 segmentation state.
 *
 * @param {string} [search] - `location.search` (e.g. `?tab=...&l4=123`) or empty string
 * @returns {Object} Parsed state: tab, l4, attr, cluster, q, brand, entity, page, etc.
 */
export function parseL4SegmentationUrl(search) {
  const p = new URLSearchParams(search || '');
  const tabRaw = p.get('tab');
  const tab = Object.values(L4_SEGMENTATION_TABS).includes(tabRaw)
    ? tabRaw
    : L4_SEGMENTATION_TABS.VIEW_RESULTS;

  const l4Raw = p.get('l4');
  let l4 = null;
  if (l4Raw != null && l4Raw !== '') {
    l4 = parsePositiveInt(l4Raw, null);
  }

  const mlL0Raw = p.get('ml_l0');
  const mlL1Raw = p.get('ml_l1');
  let mlL0 = null;
  let mlL1 = null;
  if (mlL0Raw != null && mlL0Raw !== '') {
    mlL0 = parsePositiveInt(mlL0Raw, null);
  }
  if (mlL1Raw != null && mlL1Raw !== '') {
    mlL1 = parsePositiveInt(mlL1Raw, null);
  }

  const mlSortRaw = p.get('ml_sort');
  const mlOrderRaw = p.get('ml_order');
  const validSort = Object.values(L4CategorySortField).includes(mlSortRaw);
  const validOrder = Object.values(SortDirection).includes(mlOrderRaw);

  return {
    tab,
    l4,
    attr: p.get('attr') || null,
    cluster: p.get('cluster') || null,
    q: p.get('q') || '',
    brand: p.get('brand') || null,
    entity: p.get('entity') || null,
    globalProductId: p.get('gp_id') || '',
    value: p.get('value') || null,
    page: parsePositiveInt(p.get('page'), DEFAULT_PRODUCT_PAGE),
    perPage: p.get('perPage') ? parsePositiveInt(p.get('perPage'), DEFAULT_PRODUCT_PER_PAGE) : null,
    mlPage: parsePositiveInt(p.get('ml_page'), DEFAULT_ML_PAGE),
    mlPerPage: p.get('ml_per_page')
      ? parsePositiveInt(p.get('ml_per_page'), DEFAULT_ML_PER_PAGE)
      : null,
    mlQ: p.get('ml_q') || '',
    mlL0,
    mlL1,
    mlSort: validSort ? mlSortRaw : null,
    mlOrder: validOrder ? mlOrderRaw : null,
  };
}

/**
 * Serialize structured state to a query string (leading `?` if non-empty).
 *
 * @param {Object} state - Same shape as {@link parseL4SegmentationUrl} output
 * @returns {string} Query string, e.g. `?l4=1&attr=Color`, or `''` when only defaults
 */
export function buildL4SegmentationUrl(state) {
  const p = new URLSearchParams();

  if (state.tab && state.tab !== L4_SEGMENTATION_TABS.VIEW_RESULTS) {
    p.set('tab', state.tab);
  }
  if (state.l4 != null) p.set('l4', String(state.l4));
  if (state.attr) p.set('attr', state.attr);
  if (state.cluster) p.set('cluster', state.cluster);
  if (state.q) p.set('q', state.q);
  if (state.brand) p.set('brand', state.brand);
  if (state.entity) p.set('entity', state.entity);
  if (state.globalProductId) p.set('gp_id', state.globalProductId);
  if (state.value) p.set('value', state.value);
  if (state.page != null && state.page > DEFAULT_PRODUCT_PAGE) {
    p.set('page', String(state.page));
  }
  if (state.perPage != null && state.perPage !== DEFAULT_PRODUCT_PER_PAGE) {
    p.set('perPage', String(state.perPage));
  }

  if (state.mlPage != null && state.mlPage > DEFAULT_ML_PAGE) {
    p.set('ml_page', String(state.mlPage));
  }
  if (state.mlPerPage != null && state.mlPerPage !== DEFAULT_ML_PER_PAGE) {
    p.set('ml_per_page', String(state.mlPerPage));
  }
  if (state.mlQ) p.set('ml_q', state.mlQ);
  if (state.mlL0 != null) p.set('ml_l0', String(state.mlL0));
  if (state.mlL1 != null) p.set('ml_l1', String(state.mlL1));
  if (state.mlSort && state.mlSort !== L4CategorySortField.PRODUCT_COUNT) {
    p.set('ml_sort', state.mlSort);
  }
  if (state.mlOrder && state.mlOrder !== SortDirection.DESC) {
    p.set('ml_order', state.mlOrder);
  }

  const qs = p.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Merge a partial update into the current search string and return the new query string.
 *
 * @param {string} currentSearch - Current `location.search`
 * @param {Object<string, *>} patch - Keys matching parse output; `null` or `''` removes a param
 * @returns {string} New query string from {@link buildL4SegmentationUrl}
 */
export function mergeL4SegmentationUrlParams(currentSearch, patch) {
  const base = parseL4SegmentationUrl(currentSearch || '');
  const next = { ...base };
  Object.entries(patch).forEach(([key, value]) => {
    if (value === undefined) return;
    if (value === null || value === '') {
      delete next[key];
    } else {
      next[key] = value;
    }
  });
  return buildL4SegmentationUrl(next);
}
